import axios from "axios";

import { API_URL } from "./const";
import authHeader from "./authHeader";
import authStorage from "./authStorage";

class AuthService {
  /**
   * Log in. `withCredentials` lets the backend's HttpOnly refresh-token cookie
   * (Set-Cookie on the response) stick. We only read the access token from the
   * body; the refresh token lives in the cookie and is never touched by JS.
   */
  login(email: string, password: string) {
    // E-mail is now the login identity (backend PR #76). The backend still
    // accepts `username` as a transitional alias, but we send `email`.
    const credentials = { email: email, password: password };

    return axios.post(API_URL + "auth/login", credentials, {
      withCredentials: true,
    });
  }

  /** Clear the client-side session (access token). */
  logout() {
    authStorage.clear();
  }

  /**
   * Revoke the session server-side: the refresh-token cookie (sent via
   * `withCredentials`) is denylisted and cleared, and the access token (sent as
   * Bearer) is denylisted too. Best-effort and silent: the client clears its
   * own state regardless.
   */
  serverLogout() {
    return axios.post(
      API_URL + "auth/logout",
      {},
      {
        withCredentials: true,
        headers: authHeader(),
        skipGlobalErrorAlert: true,
      }
    );
  }

  /**
   * Exchange the HttpOnly refresh-token cookie for a new access token. No body
   * and no Authorization are needed; `withCredentials` makes the browser send
   * the cookie; the cookie is rotated automatically. `skipGlobalErrorAlert`
   * keeps a failed refresh silent so the caller can fall back to login.
   */
  refresh() {
    return axios.post(
      API_URL + "auth/refresh",
      {},
      { withCredentials: true, skipGlobalErrorAlert: true }
    );
  }

  register(
    username: string,
    password: string,
    role: string,
    isAccountNonExpired: number,
    isAccountNonLocked: number,
    isCredentialsNonExpired: number,
    isEnabled: number
  ) {
    return axios.post(API_URL + "register", {
      username,
      password,
      role,
      isAccountNonExpired,
      isAccountNonLocked,
      isCredentialsNonExpired,
      isEnabled,
    });
  }

  getUserRoles() {
    return axios.get(API_URL + "user/roles", {
      headers: authHeader(),
      skipGlobalErrorAlert: true,
    });
  }
}

const authService = new AuthService();
export default authService;
